import { createContext, ReactNode, useContext, useEffect, useRef, useState } from 'react';

import { WebSocketContext } from './websocket-context/websocket-provider';
import { SUBSCRIPTIONS_KEY } from '../constants/websocket-events';
import usePersistentState from './use-persistent-state';
import { useSmartInactivityWake } from '../hooks/use-smart-inactivity-wake';
import { CurrencyTypeEnum } from '../model/currency_type.enum';

interface LatestRealizedVolsData {
  symbol: string;
  exchange: string;
  price: number;
  change_24: number;
  low_24: number;
  high_24: number;
  vol_3d: number;
  vol_1w: number;
  vol_2w: number;
  vol_1m: number;
  vol_3m: number;
  vol_6m: number;
}

interface LatestRealizedVolsProviderProps {
  children: ReactNode;
  providerKey?: string;
}

export interface LatestRealizedVolsItemType {
  value: string;
  label: string;
  isVisible: boolean;
}
const DEFAULT_VISIBLE_TOKEN_NAMES = ['BTC'];

interface LatestRealizedVolsProviderValue {
  data: LatestRealizedVolsData[];
  isLoading: boolean;
  tokenListLoading: boolean;
  timestamp: string;
  venues: string[];
  selectedVenues: string[];
  setSelectedVenues: (venues: string[]) => void;
  tokenList: LatestRealizedVolsItemType[];
  setTokenList: (tokens: LatestRealizedVolsItemType[]) => void;
  tokenPairs: LatestRealizedVolsItemType[];
  setTokenPairs: React.Dispatch<React.SetStateAction<LatestRealizedVolsItemType[]>>;
  columnOrder: string[];
  setColumnOrder: React.Dispatch<React.SetStateAction<string[]>>;
  token: CurrencyTypeEnum;
  setToken: (token: CurrencyTypeEnum) => void;
}

const LATEST_REALIZED_VOLS_ID = SUBSCRIPTIONS_KEY.TK_DATA_WIDGET_SUBSCRIPTION;
const LIST_REALIZED_VOLS_TOKENS_ID = 'LIST_REALIZED_VOLS_TOKENS_ID';

export const VENUES = ['DERIBIT', 'BINANCE', 'MEXC'];

const generateRequestId = (baseId: string, providerKey?: string): string => {
  return providerKey ? `${baseId}_${providerKey}` : baseId;
};

export const LatestRealizedVolsContext = createContext({} as LatestRealizedVolsProviderValue);

export const LatestRealizedVolsProvider = ({
  children,
  providerKey,
}: LatestRealizedVolsProviderProps) => {
  const STATE_PATH = [`LATEST_REALIZED_VOLS_PROVIDER`, `$${providerKey || 'DEFAULT'}`];

  const { sendMessage, subscribeToChannel, isConnected, unsubscribeFromChannel } =
    useContext(WebSocketContext);
  const [columnOrder, setColumnOrder] = usePersistentState<string[]>(STATE_PATH, 'columnOrder', []);
  const [data, setData] = useState<LatestRealizedVolsData[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [tokenListLoading, setTokenListLoading] = useState<boolean>(true);
  const [timestamp, setTimestamp] = useState<string>('');
  const [selectedVenues, setSelectedVenues] = usePersistentState<string[]>(
    STATE_PATH,
    'selectedVenues',
    [VENUES[0]],
  );
  const [tokenList, setTokenList] = usePersistentState<LatestRealizedVolsItemType[]>(
    STATE_PATH,
    'tokenLists',
    [],
  );

  const [tokenPairs, setTokenPairs] = usePersistentState<LatestRealizedVolsItemType[]>(
    STATE_PATH,
    'tokenPairs',
    [],
  );

  const [token, setToken] = usePersistentState<CurrencyTypeEnum>(
    STATE_PATH,
    'token',
    CurrencyTypeEnum.BITCOIN,
  );

  const hasLoaded = useRef(false);

  const handleData = (response: any) => {
    if (!response || !response.params) {
      setData([]);
      setIsLoading(false);
      return;
    }

    const { data } = response.params;

    const mappedData = (data || []).map((item: any) => ({
      ...item,

      vol_3d: item.vol_3d,
      vol_1w: item.vol_1w,
      vol_2w: item.vol_2w,
      vol_1m: item.vol_1m,
      vol_3m: item.vol_3m,
      vol_6m: item.vol_6m,
    }));

    hasLoaded.current = true;
    setData(mappedData);
    setTimestamp(new Date().toISOString());
    setIsLoading(false);
  };

  const fetchListRealisedVolTokens = () => {
    setTokenListLoading(true);
    const requestId = generateRequestId(LIST_REALIZED_VOLS_TOKENS_ID, providerKey);
    const payload = {
      jsonrpc: '2.0',
      id: requestId,
      method: 'list_realised_vol_tokens',
      params: {
        source: [],
      },
    };

    sendMessage(JSON.stringify(payload));
    subscribeToChannel(requestId, (response: any) => {
      if (!response || response.error || !response.result) {
        setTokenListLoading(false);
        return;
      }

      const { result } = response;
      if (!result.sources || !Array.isArray(result.sources) || result.sources.length === 0) {
        setTokenListLoading(false);
        return;
      }

      const allTokens = new Set<string>();

      result.sources.forEach((sourceObj: any) => {
        if (sourceObj && typeof sourceObj === 'object') {
          Object.values(sourceObj).forEach((tokenArray: any) => {
            if (Array.isArray(tokenArray)) {
              tokenArray.forEach((token: any) => {
                if (typeof token === 'string' && token?.trim()) {
                  allTokens.add(token.trim());
                }
              });
            }
          });
        }
      });

      const tokensArray = Array.from(allTokens);
      const tokensStartingWithNumber = tokensArray.filter((token) => /^\d/.test(token));
      const tokensNotStartingWithNumber = tokensArray.filter((token) => !/^\d/.test(token));

      tokensNotStartingWithNumber.sort();
      tokensStartingWithNumber.sort();

      const unifiedTokens = [...tokensNotStartingWithNumber, ...tokensStartingWithNumber];

      setTokenList((prevTokenList) => {
        const prevTokenMap = new Map(prevTokenList.map((token) => [token.value, token.isVisible]));

        const newTokenList: LatestRealizedVolsItemType[] = unifiedTokens.map((token) => ({
          value: token,
          label: token,
          isVisible: prevTokenMap.has(token)
            ? prevTokenMap.get(token)!
            : DEFAULT_VISIBLE_TOKEN_NAMES.includes(token),
        }));

        return newTokenList;
      });
      setTokenListLoading(false);
    });
  };

  const fetchLatestRealizedVols = () => {
    setIsLoading(true);
    const requestId = generateRequestId(LATEST_REALIZED_VOLS_ID, providerKey);
    const payload = {
      jsonrpc: '2.0',
      id: requestId,
      method: 'subscribe.token_data',
      params: {
        source: selectedVenues,
        symbol: tokenList.filter((t) => t.isVisible).map((t) => t.value),
      },
    };

    const unsubscribeHandler = () => {
      setData([]);
      setIsLoading(false);
    };

    sendMessage(JSON.stringify(payload));
    subscribeToChannel(requestId, handleData, unsubscribeHandler);
  };

  const reloadLatestRealizedVols = () => {
    if (!isConnected) return;
    if (tokenList.length === 0) return;

    const requestId = generateRequestId(LATEST_REALIZED_VOLS_ID, providerKey);
    unsubscribeFromChannel(requestId);
    fetchLatestRealizedVols();
  };

  useEffect(() => {
    reloadLatestRealizedVols();
  }, [isConnected, selectedVenues, tokenList, providerKey]);

  useEffect(() => {
    fetchListRealisedVolTokens();
  }, []);

  useEffect(() => {
    if (!hasLoaded.current) {
      return;
    }

    const uniqueSymbols = Array.from(new Set(data.map((item) => item.symbol)));

    setTokenPairs((prevTokenPairs) => {
      const prevMap = new Map(prevTokenPairs.map((tp) => [tp.value, tp.isVisible]));
      const newTokenPairs: LatestRealizedVolsItemType[] = uniqueSymbols.map((symbol) => ({
        value: symbol,
        label: symbol,
        isVisible: prevMap.has(symbol) ? prevMap.get(symbol)! : true,
      }));

      return newTokenPairs;
    });
  }, [data]);

  const onWakeUp = () => {
    reloadLatestRealizedVols();
  };

  useSmartInactivityWake(onWakeUp);

  return (
    <LatestRealizedVolsContext.Provider
      value={{
        data,
        isLoading,
        tokenListLoading,
        timestamp,
        venues: VENUES,
        selectedVenues,
        setSelectedVenues,
        tokenList,
        setTokenList,
        tokenPairs,
        setTokenPairs,
        columnOrder,
        setColumnOrder,
        token,
        setToken,
      }}
    >
      {children}
    </LatestRealizedVolsContext.Provider>
  );
};
